import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ContentArticleStatus } from '@xing-com/crate-common-graphql-types';
import { useEditStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';

import type { ArticleCardView } from '../../../types/types';
import { ArticleState } from '../article-state/article-state';

import {
  ArticleCardTopWrapper,
  ButtonsContainer,
  InfoContainer,
} from './article-card-top.styles';
import { ArticleEditButton } from './article-controls/article-edit-button';
import { ArticleFeatureButton } from './article-controls/article-feature-button';
import { ArticleHideButton } from './article-controls/article-hide-button';
import { ArticleRemoveButton } from './article-controls/article-remove-button';
import { ArticleShowButton } from './article-controls/article-show-button';
import { ArticleUnfeatureButton } from './article-controls/article-unfeature-button';

type Props = {
  article: ArticleCardView;
};

export const ArticleCardTop: React.FC<Props> = ({ article }) => {
  const { formatDate } = useIntl();
  const isEditing = useEditStore(({ isEditing }) => isEditing);
  const { publicationState, publishedAt, updatedAt, isFeatured } = article;

  const [actionLoading, setActionLoading] = useState(false);

  const isPublished = publicationState === ContentArticleStatus.Published;
  const isHidden = publicationState === ContentArticleStatus.Disabled;
  const articleDate = publishedAt ?? updatedAt;

  useEffect(() => {
    return () => setActionLoading(false);
  }, []);

  return (
    <ArticleCardTopWrapper>
      <InfoContainer>
        {isEditing ? (
          <span>
            <ArticleState state={publicationState} />
          </span>
        ) : null}
        {articleDate ? (
          <span>
            {formatDate(articleDate, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
        ) : null}
        {isFeatured ? (
          <strong>
            <FormattedMessage id="EP_ARTICLES_MODULE_PINNED_LABEL" />
          </strong>
        ) : null}
      </InfoContainer>
      {isEditing ? (
        <ButtonsContainer>
          {isPublished ? (
            isFeatured ? (
              <ArticleUnfeatureButton
                article={article}
                disabled={actionLoading}
                onLoadingChange={setActionLoading}
              />
            ) : (
              <ArticleFeatureButton
                article={article}
                disabled={actionLoading}
                onLoadingChange={setActionLoading}
              />
            )
          ) : null}
          <ArticleEditButton article={article} disabled={actionLoading} />
          {isPublished ? (
            <ArticleHideButton
              article={article}
              disabled={actionLoading}
              onLoadingChange={setActionLoading}
            />
          ) : null}
          {isHidden ? (
            <ArticleShowButton
              article={article}
              disabled={actionLoading}
              onLoadingChange={setActionLoading}
            />
          ) : null}

          <ArticleRemoveButton
            article={article}
            disabled={actionLoading}
            onLoadingChange={setActionLoading}
          />
        </ButtonsContainer>
      ) : null}
    </ArticleCardTopWrapper>
  );
};
